import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../context/LanguageContext';

// ---------- animations ----------
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const cardReveal = keyframes`
  0%   { opacity: 0; transform: scale(0.85) translateY(20px); }
  60%  { opacity: 1; transform: scale(1.04) translateY(-4px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

// ---------- layout ----------
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--space-6) var(--space-5);
  max-width: 880px;
  margin: 0 auto;
  background:
    linear-gradient(180deg, rgba(21, 26, 51, 0.82) 0%, rgba(11, 16, 32, 0.92) 100%);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-elevated);
  animation: ${fadeUp} 0.7s var(--ease-soft);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 6px;
    border-radius: calc(var(--radius-lg) - 6px);
    border: 1px solid var(--color-border-gold);
    pointer-events: none;
    opacity: 0.35;
  }

  @media (max-width: 600px) {
    padding: var(--space-5) var(--space-4);
  }
`;

const Title = styled.h1`
  font-family: var(--font-display);
  color: var(--color-gold-soft);
  margin-bottom: var(--space-5);
  text-align: center;
  font-size: clamp(1.8rem, 3.5vw, 2.4rem);
  font-weight: 600;
  text-shadow: 0 0 22px rgba(156, 107, 255, 0.4);
`;

const QuestionSection = styled.div`
  width: 100%;
  margin-bottom: var(--space-5);
`;

const QuestionLabel = styled.h3`
  font-family: var(--font-display);
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-size: 0.85rem;
  margin-bottom: var(--space-3);
  color: var(--color-gold);
`;

const Question = styled.div`
  background: rgba(7, 9, 26, 0.55);
  padding: var(--space-4) var(--space-5);
  border-left: 3px solid var(--color-accent-violet);
  border-radius: var(--radius-md);
  font-size: var(--fs-md);
  color: var(--color-text-primary);
  line-height: var(--lh-normal);
  font-style: italic;
`;

const CardsSection = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--space-5);
  margin-bottom: var(--space-6);
  width: 100%;
`;

const CardItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 180px;
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: rgba(11, 16, 32, 0.5);
  border: 1px solid var(--color-border-soft);
  animation: ${fadeUp} 0.6s var(--ease-soft);
`;

const PositionLabel = styled.h4`
  font-family: var(--font-display);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 0.8rem;
  margin-bottom: var(--space-3);
  color: var(--color-gold);
`;

const CardImage = styled.div`
  width: 130px;
  height: 200px;
  border-radius: 10px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  margin-bottom: var(--space-3);
  transform: ${props => props.reversed ? 'rotate(180deg)' : 'rotate(0deg)'};
  animation: ${cardReveal} 0.7s var(--ease-soft);
  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.6),
    inset 0 0 0 1px rgba(212, 175, 55, 0.45),
    0 0 18px rgba(156, 107, 255, 0.22);
`;

const CardName = styled.h5`
  text-align: center;
  font-family: var(--font-display);
  font-size: 0.9rem;
  letter-spacing: 0.06em;
  color: var(--color-text-primary);
  margin: 0;
`;

const InterpretationSection = styled.div`
  background:
    linear-gradient(180deg, rgba(11, 16, 32, 0.7) 0%, rgba(7, 9, 26, 0.85) 100%);
  padding: var(--space-6);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-6);
  width: 100%;
  white-space: pre-line;
  border: 1px solid var(--color-border-strong);
  border-left: 3px solid var(--color-gold);
  box-shadow: var(--shadow-card);

  @media (max-width: 600px) {
    padding: var(--space-5);
  }
`;

const InterpretationTitle = styled.h3`
  font-family: var(--font-display);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: var(--space-4);
  color: var(--color-gold-soft);
  font-size: 1.05rem;
  border-bottom: 1px solid var(--color-border-gold);
  padding-bottom: var(--space-3);
`;

const InterpretationText = styled.div`
  font-family: var(--font-body);
  font-size: 1.05rem;
  line-height: var(--lh-reading);
  color: var(--color-text-primary);
  text-align: left;
`;

const ErrorMessage = styled.div`
  color: #ffb4b4;
  font-size: 1rem;
  padding: var(--space-4);
  background-color: rgba(140, 30, 30, 0.25);
  border: 1px solid rgba(255, 90, 90, 0.4);
  border-radius: var(--radius-md);
`;

const BackButton = styled.button`
  padding: 14px 36px;
  background: linear-gradient(135deg, var(--color-accent-purple), var(--color-accent-violet));
  color: var(--color-text-primary);
  border: 1px solid rgba(246, 231, 193, 0.4);
  border-radius: var(--radius-pill);
  font-family: var(--font-display);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform var(--dur-base) var(--ease-soft),
              box-shadow var(--dur-base) var(--ease-soft),
              filter var(--dur-base) var(--ease-soft);
  box-shadow: 0 6px 20px rgba(110, 76, 207, 0.5);

  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.1);
    box-shadow: var(--glow-violet-strong);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--color-gold);
    outline-offset: 3px;
  }
`;

// ---------- component (LOGIC UNCHANGED) ----------
const Interpretation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { question, cards, interpretation, error } = location.state || {};

  const positions = [t('positions.past'), t('positions.present'), t('positions.future')];

  useEffect(() => {
    if (!cards || cards.length === 0) {
      navigate('/');
    }
  }, [cards, navigate]);

  if (!cards || cards.length === 0) {
    return null;
  }

  return (
    <Container>
      <Title>{t('interpretation.title')}</Title>

      <QuestionSection>
        <QuestionLabel>{t('interpretation.questionLabel')}</QuestionLabel>
        <Question>{question}</Question>
      </QuestionSection>

      <CardsSection>
        {cards.map((card, index) => {
          const position = positions[index];
          const orientation = card.reversed
            ? `(${t('orientation.reversed')})`
            : `(${t('orientation.upright')})`;
          return (
            <CardItem key={index}>
              <PositionLabel>{position}</PositionLabel>
              <CardImage src={card.image} alt={card.name} reversed={card.reversed} />
              <CardName>
                {card.name} {orientation}
              </CardName>
            </CardItem>
          );
        })}
      </CardsSection>

      <InterpretationSection>
        <InterpretationTitle>{t('interpretation.interpretationTitle')}</InterpretationTitle>
        {error ? (
          <ErrorMessage>{t('interpretation.errorMessage')}</ErrorMessage>
        ) : (
          <InterpretationText>{interpretation || t('interpretation.loadingMessage')}</InterpretationText>
        )}
      </InterpretationSection>

      <BackButton onClick={() => navigate('/')}>
        {t('interpretation.newReadingButton')}
      </BackButton>
    </Container>
  );
};

export default Interpretation;
